/**
 * Match Element Grader
 */

import size from 'lodash/size';
import { createScoringEngine } from '@ai-content-hub/content-core';
import type { ScoringConfig } from '@ai-content-hub/content-core';
import type {
    ElementGrader,
    GradingConfig,
    GradingResult,
    ValidationResult,
    ValidationError,
} from '@ai-content-hub/content-kit';
import type { MatchElementData, MatchAnswer, MatchItem, MatchOption } from './schema';
import { getItemText, getOptionText } from './schema';

export const matchGrader: ElementGrader<MatchElementData, MatchAnswer> = {
    grade(data: MatchElementData, answer: MatchAnswer, config: GradingConfig = {}): GradingResult {
        const { items } = data;

        if (!items || items.length === 0) {
            return {
                isCorrect: false,
                score: 0,
                max: 0,
                feedback: 'No items to match.',
            };
        }

        const scoringConfig: ScoringConfig = {
            max: config.max ?? items.length,
            partialCreditEnabled: config.partialCredit ?? true,
        };

        const engine = createScoringEngine(scoringConfig);
        const correctPairs = Object.fromEntries(
            items.map((item) => [item.id, item.correctOptionId])
        );
        const result = engine.gradeMatching(answer, correctPairs);
        const correctCount = (result.details?.correctCount as number) ?? 0;

        return {
            ...result,
            feedback: result.isCorrect
                ? 'Correct! All matches are correct.'
                : `You got ${correctCount} of ${items.length} matches correct.`,
            details: { ...result.details, totalItems: items.length },
        };
    },

    getCorrectAnswer(data: MatchElementData): MatchAnswer {
        const answer: MatchAnswer = {};
        for (const item of data.items || []) {
            answer[item.id] = item.correctOptionId;
        }
        return answer;
    },

    validate(data: MatchElementData): ValidationResult {
        const errors: ValidationError[] = [];

        if (!data.items || size(data.items) < 2) {
            errors.push({
                field: 'items',
                message: 'At least 2 items are required',
                code: 'minItems',
            });
        } else {
            data.items.forEach((item: MatchItem, index: number) => {
                const itemText = getItemText(item);
                if (!itemText || !itemText.trim()) {
                    const hasMedia =
                        item.content.type === 'image' &&
                        'src' in item.content &&
                        item.content.src;
                    const hasAudio =
                        item.content.type === 'audio' &&
                        'src' in item.content &&
                        item.content.src;
                    if (!hasMedia && !hasAudio) {
                        errors.push({
                            field: `items.${index}.content`,
                            message: `Item ${index + 1} content is required`,
                            code: 'required',
                        });
                    }
                }
                if (!item.correctOptionId) {
                    errors.push({
                        field: `items.${index}.correctOptionId`,
                        message: `Item ${index + 1} must have a correct answer selected`,
                        code: 'required',
                    });
                }
            });
        }

        if (!data.options || size(data.options) < 2) {
            errors.push({
                field: 'options',
                message: 'At least 2 options are required',
                code: 'minItems',
            });
        } else {
            data.options.forEach((option: MatchOption, index: number) => {
                const optionText = getOptionText(option);
                const hasMedia =
                    option.content.type === 'image' &&
                    'src' in option.content &&
                    option.content.src;
                const hasAudio =
                    option.content.type === 'audio' &&
                    'src' in option.content &&
                    option.content.src;
                if (
                    (!optionText || !optionText.trim()) &&
                    !hasMedia &&
                    !hasAudio
                ) {
                    errors.push({
                        field: `options.${index}.content`,
                        message: `Option ${option.letter} content is required`,
                        code: 'required',
                    });
                }
            });
        }

        return {
            isValid: size(errors) === 0,
            errors,
        };
    },

    /**
     * Each match pair (item) is 1 gradable question.
     */
    countQuestions(data: MatchElementData): number {
        return (data.items ?? []).length;
    },
};

export default matchGrader;
